"use client";

import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TAXRISE_SUPPORT_PHONE_DISPLAY,
  TAXRISE_SUPPORT_PHONE_TEL,
} from "@/lib/observability/support";

export type ErrorFallbackAction = {
  label: string;
  onClick: () => void;
  href?: string;
};

export function BookingErrorFallback({
  title,
  body,
  digest,
  showSupportPhone,
  actions,
}: {
  title: string;
  body: string;
  digest?: string;
  showSupportPhone?: boolean;
  actions: ErrorFallbackAction[];
}) {
  return (
    <div
      data-testid="booking-error-boundary"
      className="flex flex-col items-center py-8 text-center"
    >
      <RefreshCw aria-hidden="true" className="mb-5 size-10 text-primary" />
      <h2 className="max-w-lg text-xl font-semibold text-balance">{title}</h2>
      <p className="mt-3 max-w-md text-pretty text-muted-foreground">{body}</p>
      <div className="mt-5 flex flex-col items-center gap-3">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {actions.map((action, index) => {
            const variant = index === 0 ? "default" : "outline";
            if (action.href) {
              return (
                <Button
                  key={action.label}
                  variant={variant}
                  nativeButton={false}
                  render={<a href={action.href} />}
                >
                  {action.label}
                </Button>
              );
            }
            return (
              <Button key={action.label} variant={variant} onClick={action.onClick}>
                {action.label}
              </Button>
            );
          })}
        </div>
        {showSupportPhone ? (
          <div className="flex flex-col items-center gap-1">
            <Button
              variant="outline"
              nativeButton={false}
              render={<a href={`tel:${TAXRISE_SUPPORT_PHONE_TEL}`} />}
            >
              Call TaxRise
            </Button>
            <p className="text-sm text-muted-foreground tabular-nums">
              {TAXRISE_SUPPORT_PHONE_DISPLAY}
            </p>
          </div>
        ) : null}
        {digest ? (
          <p className="text-xs text-muted-foreground tabular-nums">
            Reference <span data-testid="support-reference">{digest}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}
